import React, { useState, useEffect } from 'react';
import { FiUser, FiActivity, FiTarget, FiCalendar, FiCheck, FiX, FiChevronRight, FiSave } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';
import nutritionService from '../../../services/nutritionService';
import {
  calculateBMR,
  calculateBMRMifflinStJeor,
  calculateTDEE,
  calculateMacros,
  calculateDailyCalorieTarget,
  activityLevels,
  nutritionGoals
} from '../../../utils/nutritionCalculations';
import './NutritionPlanWizard.css';

const NutritionPlanWizard = ({ clientId, clientProfile, onClose, onSave }) => {
  const { currentUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);

  // Step 1: Client Body Composition
  const [bodyData, setBodyData] = useState({
    weight: clientProfile?.bodyMeasurements?.weight || '',
    height: clientProfile?.bodyMeasurements?.height || '',
    age: clientProfile?.age || '',
    gender: clientProfile?.gender || 'male',
    bodyFatPercentage: clientProfile?.bodyMeasurements?.bodyFat || '',
    hasBodyFat: false
  });

  // Step 2: Activity Level
  const [selectedActivity, setSelectedActivity] = useState('moderate');

  // Step 3: Goal
  const [selectedGoal, setSelectedGoal] = useState('maintain');

  // Step 4: Calculated Results
  const [calculations, setCalculations] = useState({
    bmr: 0,
    tdee: 0,
    targetCalories: 0,
    macros: null
  });

  // Step 5: Meal Planning
  const [mealPlan, setMealPlan] = useState({
    mealsPerDay: 3,
    startDate: new Date().toISOString().split('T')[0],
    duration: 'ongoing' // 'ongoing', '30days', '60days', '90days'
  });

  // Template name (only for templates)
  const [templateName, setTemplateName] = useState('');
  const isTemplate = !clientId;

  // Calculate nutrition when body data or goals change
  useEffect(() => {
    if (bodyData.weight && bodyData.height && bodyData.age) {
      calculateNutrition();
    }
  }, [bodyData, selectedActivity, selectedGoal]);

  const calculateNutrition = () => {
    const weight = parseFloat(bodyData.weight);
    const height = parseFloat(bodyData.height);
    const age = parseInt(bodyData.age);
    const bodyFat = bodyData.hasBodyFat ? parseFloat(bodyData.bodyFatPercentage) : null;

    // Calculate BMR
    let bmr;
    if (bodyFat && bodyFat > 0) {
      bmr = calculateBMR(weight, bodyFat);
    } else {
      bmr = calculateBMRMifflinStJeor(weight, height, age, bodyData.gender);
    }

    // Get activity multiplier
    const activityLevel = activityLevels.find(a => a.value === selectedActivity);
    const activityMultiplier = activityLevel ? activityLevel.multiplier : 1.55;

    // Calculate TDEE
    const tdee = calculateTDEE(bmr, activityMultiplier);

    // Calculate target calories based on goal
    const targetCalories = calculateDailyCalorieTarget(tdee, selectedGoal);

    // Calculate macros
    const leanMass = bodyFat ? weight * (1 - bodyFat / 100) : weight * 0.85; // Estimate if no body fat
    const macros = calculateMacros(targetCalories, selectedGoal, weight, leanMass);

    setCalculations({
      bmr: Math.round(bmr),
      tdee: Math.round(tdee),
      targetCalories,
      macros
    });
  };

  const buildPlanData = () => {
    const plan = {
      clientId: clientId || null,
      coachId: currentUser.uid,
      isTemplate: isTemplate,
      bodyData: {
        weight: parseFloat(bodyData.weight) || 0,
        height: parseFloat(bodyData.height) || 0,
        age: parseInt(bodyData.age) || 0,
        gender: bodyData.gender,
        bodyFatPercentage: bodyData.hasBodyFat ? parseFloat(bodyData.bodyFatPercentage) : null
      },
      activityLevel: selectedActivity,
      goal: selectedGoal,
      calculations: {
        bmr: calculations.bmr,
        tdee: calculations.tdee,
        targetCalories: calculations.targetCalories,
        macros: calculations.macros
      },
      mealPlan: {
        mealsPerDay: mealPlan.mealsPerDay,
        startDate: mealPlan.startDate,
        duration: mealPlan.duration
      },
      status: isTemplate ? 'template' : 'active',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Only add name if it's a template
    if (isTemplate) {
      plan.name = templateName || 'Nutrition Template';
    }

    return plan;
  };

  const handleSavePlan = async () => {
    setSaving(true);
    try {
      const plan = buildPlanData();

      await nutritionService.createNutritionPlan(plan);
      alert(isTemplate ? 'Nutrition template created successfully!' : 'Nutrition plan created successfully!');

      if (onSave) {
        onSave(plan);
      }

      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('[NutritionPlanWizard] Error saving plan:', error);
      alert('Error saving nutrition plan. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProgress = async () => {
    try {
      const plan = buildPlanData();
      plan.status = 'draft'; // Mark as draft when saving progress

      await nutritionService.createNutritionPlan(plan);
      alert('Progress saved successfully!');
    } catch (error) {
      console.error('[NutritionPlanWizard] Error saving progress:', error);
      alert('Error saving progress. Please try again.');
    }
  };

  const canProceedFromStep = (step) => {
    switch(step) {
      case 1:
        return bodyData.weight && bodyData.height && bodyData.age;
      case 2:
        return selectedActivity;
      case 3:
        return selectedGoal;
      case 4:
        return calculations.macros !== null;
      default:
        return true;
    }
  };

  const renderStep1 = () => (
    <div className="wizard-step-content">
      <h3>{isTemplate ? 'Template Body Composition' : 'Client Body Composition'}</h3>
      <p className="step-description">
        {isTemplate ? 'Enter typical body measurements for this template' : 'Enter the client\'s current body measurements'}
      </p>

      {isTemplate && (
        <div className="template-name-section">
          <div className="form-group">
            <label>Template Name *</label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="e.g., Weight Loss - Male 80kg"
              className="template-name-input"
            />
            <p className="field-hint">Give this template a descriptive name for easy identification</p>
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="form-group">
          <label>Weight (kg) *</label>
          <input
            type="number"
            step="0.1"
            value={bodyData.weight}
            onChange={(e) => setBodyData({ ...bodyData, weight: e.target.value })}
            placeholder="e.g., 75.5"
          />
        </div>

        <div className="form-group">
          <label>Height (cm) *</label>
          <input
            type="number"
            value={bodyData.height}
            onChange={(e) => setBodyData({ ...bodyData, height: e.target.value })}
            placeholder="e.g., 175"
          />
        </div>

        <div className="form-group">
          <label>Age *</label>
          <input
            type="number"
            value={bodyData.age}
            onChange={(e) => setBodyData({ ...bodyData, age: e.target.value })}
            placeholder="e.g., 30"
          />
        </div>

        <div className="form-group">
          <label>Gender *</label>
          <select
            value={bodyData.gender}
            onChange={(e) => setBodyData({ ...bodyData, gender: e.target.value })}
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
      </div>

      <div className="body-fat-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={bodyData.hasBodyFat}
            onChange={(e) => setBodyData({ ...bodyData, hasBodyFat: e.target.checked })}
          />
          <span>I have body fat percentage data (more accurate)</span>
        </label>

        {bodyData.hasBodyFat && (
          <div className="form-group">
            <label>Body Fat Percentage (%)</label>
            <input
              type="number"
              step="0.1"
              value={bodyData.bodyFatPercentage}
              onChange={(e) => setBodyData({ ...bodyData, bodyFatPercentage: e.target.value })}
              placeholder="e.g., 15.5"
            />
            <p className="field-hint">
              {bodyData.hasBodyFat ? 'Will use Katch-McArdle formula (most accurate)' : 'Will use Mifflin-St Jeor formula'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step-content">
      <h3>Activity Level</h3>
      <p className="step-description">Select the client's typical activity level</p>

      <div className="activity-options">
        {activityLevels.map(level => (
          <div
            key={level.value}
            className={`activity-card ${selectedActivity === level.value ? 'selected' : ''}`}
            onClick={() => setSelectedActivity(level.value)}
          >
            <div className="activity-header">
              <FiActivity />
              <h4>{level.label.split('(')[0].trim()}</h4>
            </div>
            <p className="activity-description">{level.label.match(/\((.*)\)/)?.[1] || ''}</p>
            <span className="activity-multiplier">×{level.multiplier} TDEE</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step-content">
      <h3>Nutrition Goal</h3>
      <p className="step-description">Select the client's primary nutrition goal</p>

      <div className="goal-options">
        {nutritionGoals.map(goal => (
          <div
            key={goal.value}
            className={`goal-card ${selectedGoal === goal.value ? 'selected' : ''}`}
            onClick={() => setSelectedGoal(goal.value)}
          >
            <div className="goal-header">
              <FiTarget />
              <h4>{goal.label}</h4>
            </div>
            <p className="goal-adjustment">
              {goal.calorieAdjustment > 0 ? '+' : ''}{goal.calorieAdjustment} calories/day
            </p>
            <p className="goal-rate">
              {goal.weeklyChange !== 0 ? `${Math.abs(goal.weeklyChange / 7700).toFixed(2)} kg/week` : 'Maintain current weight'}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-step-content">
      <h3>Calculated Nutrition Targets</h3>
      <p className="step-description">Review the calculated targets for your client</p>

      <div className="calculations-display">
        <div className="calc-card">
          <label>Basal Metabolic Rate (BMR)</label>
          <div className="calc-value">{calculations.bmr} cal/day</div>
          <p className="calc-description">Calories burned at rest</p>
        </div>

        <div className="calc-card">
          <label>Total Daily Energy Expenditure (TDEE)</label>
          <div className="calc-value">{calculations.tdee} cal/day</div>
          <p className="calc-description">Calories burned with activity</p>
        </div>

        <div className="calc-card primary">
          <label>Target Daily Calories</label>
          <div className="calc-value">{calculations.targetCalories} cal/day</div>
          <p className="calc-description">Adjusted for {selectedGoal} goal</p>
        </div>
      </div>

      {calculations.macros && (
        <div className="macros-display">
          <h4>Daily Macro Targets</h4>
          <div className="macro-cards">
            <div className="macro-card protein">
              <div className="macro-label">Protein</div>
              <div className="macro-amount">{calculations.macros.protein.grams}g</div>
              <div className="macro-calories">{calculations.macros.protein.calories} cal</div>
              <div className="macro-percentage">{calculations.macros.protein.percentage}%</div>
            </div>

            <div className="macro-card carbs">
              <div className="macro-label">Carbs</div>
              <div className="macro-amount">{calculations.macros.carbs.grams}g</div>
              <div className="macro-calories">{calculations.macros.carbs.calories} cal</div>
              <div className="macro-percentage">{calculations.macros.carbs.percentage}%</div>
            </div>

            <div className="macro-card fats">
              <div className="macro-label">Fats</div>
              <div className="macro-amount">{calculations.macros.fat.grams}g</div>
              <div className="macro-calories">{calculations.macros.fat.calories} cal</div>
              <div className="macro-percentage">{calculations.macros.fat.percentage}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep5 = () => (
    <div className="wizard-step-content">
      <h3>Meal Plan Settings</h3>
      <p className="step-description">Configure the meal plan structure</p>

      <div className="form-grid">
        <div className="form-group">
          <label>Meals Per Day</label>
          <select
            value={mealPlan.mealsPerDay}
            onChange={(e) => setMealPlan({ ...mealPlan, mealsPerDay: parseInt(e.target.value) })}
          >
            <option value={3}>3 Meals</option>
            <option value={4}>4 Meals (3 + Snack)</option>
            <option value={5}>5 Meals (3 + 2 Snacks)</option>
            <option value={6}>6 Meals (3 + 3 Snacks)</option>
          </select>
        </div>

        <div className="form-group">
          <label>Start Date</label>
          <input
            type="date"
            value={mealPlan.startDate}
            onChange={(e) => setMealPlan({ ...mealPlan, startDate: e.target.value })}
          />
        </div>

        <div className="form-group">
          <label>Plan Duration</label>
          <select
            value={mealPlan.duration}
            onChange={(e) => setMealPlan({ ...mealPlan, duration: e.target.value })}
          >
            <option value="ongoing">Ongoing</option>
            <option value="30days">30 Days</option>
            <option value="60days">60 Days</option>
            <option value="90days">90 Days</option>
          </select>
        </div>
      </div>

      <div className="meal-distribution-preview">
        <h4>Meal Distribution</h4>
        <p className="preview-description">
          Daily calories ({calculations.targetCalories}) will be distributed across {mealPlan.mealsPerDay} meals
        </p>
        <div className="distribution-bars">
          {getMealDistribution().map((meal, index) => (
            <div key={index} className="distribution-bar">
              <span className="meal-name">{meal.name}</span>
              <div className="bar-container">
                <div className="bar-fill" style={{ width: `${meal.percentage}%` }}></div>
              </div>
              <span className="meal-calories">{meal.calories} cal ({meal.percentage}%)</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const getMealDistribution = () => {
    const distributions = {
      3: [
        { name: 'Breakfast', percentage: 35 },
        { name: 'Lunch', percentage: 35 },
        { name: 'Dinner', percentage: 30 }
      ],
      4: [
        { name: 'Breakfast', percentage: 25 },
        { name: 'Lunch', percentage: 35 },
        { name: 'Dinner', percentage: 30 },
        { name: 'Snack', percentage: 10 }
      ],
      5: [
        { name: 'Breakfast', percentage: 25 },
        { name: 'Morning Snack', percentage: 10 },
        { name: 'Lunch', percentage: 30 },
        { name: 'Afternoon Snack', percentage: 10 },
        { name: 'Dinner', percentage: 25 }
      ],
      6: [
        { name: 'Breakfast', percentage: 20 },
        { name: 'Morning Snack', percentage: 10 },
        { name: 'Lunch', percentage: 25 },
        { name: 'Afternoon Snack', percentage: 10 },
        { name: 'Dinner', percentage: 25 },
        { name: 'Evening Snack', percentage: 10 }
      ]
    };

    const distribution = distributions[mealPlan.mealsPerDay] || distributions[3];
    return distribution.map(meal => ({
      ...meal,
      calories: Math.round(calculations.targetCalories * meal.percentage / 100)
    }));
  };

  return (
    <div className="nutrition-plan-wizard">
      <div className="wizard-header">
        <div className="wizard-title">
          <h2>{isTemplate ? 'Create Nutrition Template' : 'Create Nutrition Plan'}</h2>
          {!isTemplate && clientProfile && (
            <span className="client-name">for {clientProfile.name || 'Client'}</span>
          )}
          {isTemplate && (
            <span className="client-name">Reusable template for multiple clients</span>
          )}
        </div>
        <button className="close-btn" onClick={onClose}>
          <FiX />
        </button>
      </div>

      {/* Progress Steps */}
      <div className="wizard-progress">
        {[1, 2, 3, 4, 5].map(step => (
          <div
            key={step}
            className={`progress-step ${currentStep === step ? 'active' : ''} ${currentStep > step ? 'completed' : ''}`}
            onClick={() => canProceedFromStep(step - 1) && setCurrentStep(step)}
          >
            <div className="step-circle">
              {currentStep > step ? <FiCheck /> : step}
            </div>
            <span className="step-label">
              {step === 1 && 'Body Data'}
              {step === 2 && 'Activity'}
              {step === 3 && 'Goal'}
              {step === 4 && 'Targets'}
              {step === 5 && 'Meal Plan'}
            </span>
          </div>
        ))}
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {currentStep === 1 && renderStep1()}
        {currentStep === 2 && renderStep2()}
        {currentStep === 3 && renderStep3()}
        {currentStep === 4 && renderStep4()}
        {currentStep === 5 && renderStep5()}
      </div>

      {/* Navigation */}
      <div className="wizard-footer">
        {currentStep > 1 && (
          <button
            className="prev-btn"
            onClick={() => setCurrentStep(currentStep - 1)}
          >
            Previous
          </button>
        )}

        <button
          className="save-progress-btn"
          onClick={handleSaveProgress}
          disabled={!bodyData.weight || !bodyData.height || !bodyData.age}
        >
          <FiSave /> Save Progress
        </button>

        {currentStep < 5 ? (
          <button
            className="next-btn"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceedFromStep(currentStep)}
          >
            Next <FiChevronRight />
          </button>
        ) : (
          <button
            className="save-btn"
            onClick={handleSavePlan}
            disabled={saving}
          >
            <FiSave /> {saving ? 'Saving...' : 'Create Plan'}
          </button>
        )}
      </div>
    </div>
  );
};

export default NutritionPlanWizard;
